














































import { computed, defineComponent, onMounted, ref } from '@vue/composition-api';
import type { PropType } from 'vue';
import { storeToRefs } from 'pinia';
import { InputType, ButtonType } from '@getaccept/lib-ui';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { AuthenticationStepType } from '@getaccept/lib-shared/src/authentication/types/enums/authentication-step-type';
import { validateEmail } from '@getaccept/lib-shared/src/helpers';
import type { IdentifyRecipientPayload } from '@getaccept/lib-shared/src/universal-link/types/identify-recipient-payload';
import { RecipientsHelper } from '@getaccept/lib-shared/src/recipients/helpers/recipients.helper';
import type { VerifyLinkResponse } from '@getaccept/lib-shared/src/universal-link/types/verify-link-response';
import { useUniversalLink } from '../composables/universal-link.composable';
import { useSessionStore } from '../../session/store/session.store';
import ParticipantDetails from './ParticipantDetails.vue';

export default defineComponent({
  components: {
    ParticipantDetails,
  },
  props: {
    consents: {
      type: Array as PropType<
        {
          type: AuthenticationStepType;
          value: string | boolean;
        }[]
      >,
      default: () => [],
    },
    email: {
      type: String,
      default: '',
    },
    canGoBack: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
    verifyLinkResponse: {
      type: Object as PropType<VerifyLinkResponse>,
      default: () => ({}),
    },
  },
  setup(props) {
    const { getRedirectUrl, loadingRedirectURL, redirectURLError } = useUniversalLink();
    const sessionsStore = useSessionStore();
    const { entityId, documentId } = storeToRefs(sessionsStore);
    const updatedEmail = ref(props.email);
    const updatedParticipant = ref<{
      firstName?: string;
      lastName?: string;
      title?: string;
      companyName?: string;
    }>({ firstName: '', lastName: '' });

    const gdprConsent = computed(
      () =>
        props.consents.find(({ type }) => type === AuthenticationStepType.GdprConsent)?.value ??
        true
    );

    const trackingConsent = computed(
      () =>
        (props.consents.find(({ type }) => type === AuthenticationStepType.TrackingConsent)
          ?.value as '0' | '1') ?? null
    );

    const invalidEmail = ref(false);

    const handleSubmit = () => {
      if (!updatedEmail.value || loadingRedirectURL.value || props.disabled) {
        return;
      }

      if (!validateEmail(updatedEmail.value?.toLowerCase())) {
        invalidEmail.value = true;
        return;
      }

      getRedirectUrl(
        Boolean(gdprConsent.value),
        props.verifyLinkResponse.documentId || documentId.value,
        { ...updatedParticipant.value, email: updatedEmail.value },
        props.verifyLinkResponse.entityId || entityId.value,
        trackingConsent.value
      );
    };

    const emailInvalidMessage = computed(() =>
      redirectURLError.value || invalidEmail.value ? t('enter_a_valid_email') : ''
    );

    const handleInput = (updatePayload: IdentifyRecipientPayload) => {
      updatedParticipant.value = { ...updatedParticipant, ...updatePayload };
    };

    const setParticipantValuesFromProp = (email: string) => {
      const participantData = RecipientsHelper.getRecipientDataFromEmail(
        RecipientsHelper.formatSearchString(email)
      );
      updatedParticipant.value.firstName = participantData.firstName;
      updatedParticipant.value.lastName = participantData.lastName;
    };

    onMounted(() => {
      if (!props.email) {
        return;
      }

      setParticipantValuesFromProp(props.email);
    });

    return {
      t,
      updatedParticipant,
      updatedEmail,
      handleInput,
      handleSubmit,
      InputType,
      ButtonType,
      loadingRedirectURL,
      emailInvalidMessage,
    };
  },
});
